import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { researchAPI, profilesAPI } from '../services/api';
import { useToast } from './Toast';
import ResearchReportBody from './ResearchReportBody';
import '../styles/WorkspaceChrome.css';
import './ResearchReports.css';

function profileHeading(report, profiles) {
  const rc = report.run_context || {};
  if (rc.kind === 'content_cluster' || rc.foundation === 'existing_content') {
    const title = rc.source?.title;
    return title ? `Cluster: ${title}` : 'Content cluster research';
  }
  if (report.profile_id != null) {
    const p = profiles.find((x) => x.id === report.profile_id);
    return p ? p.name : `Saved #${report.profile_id}`;
  }
  if (rc.brief_name) return rc.brief_name;
  if (rc.title?.trim()) return rc.title.trim();
  return 'Research brief';
}

function ResearchReportDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [report, setReport] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadReport = useCallback(async () => {
    const rid = parseInt(id, 10);
    if (Number.isNaN(rid)) {
      setError('Invalid report id');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const [repRes, profRes] = await Promise.all([
        researchAPI.getReport(rid),
        profilesAPI.getProfiles(),
      ]);
      setReport(repRes.data);
      setProfiles(profRes.data);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || err.message || 'Failed to load report');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleArchiveToggle = async () => {
    if (!report) return;
    const next = !(report.workspace_archived === true);
    try {
      await researchAPI.patchReport(report.id, { workspace_archived: next });
      showToast(next ? 'Report archived' : 'Report restored', 'success');
      if (next) {
        navigate('/research/reports');
      } else {
        await loadReport();
      }
    } catch (err) {
      showToast(err.response?.data?.error || err.message || 'Update failed', 'error');
    }
  };

  const handleDelete = async () => {
    if (
      !report ||
      !window.confirm(`Delete Report #${report.id}? This cannot be undone.`)
    )
      return;
    try {
      await researchAPI.deleteReport(report.id);
      showToast('Report deleted successfully', 'success');
      navigate('/research/reports');
    } catch (err) {
      showToast(
        `Failed to delete report: ${err.response?.data?.error || err.message || 'Unknown error'}`,
        'error'
      );
    }
  };

  const basePath = '/research/reports';

  if (loading) {
    return <div className="loading">Loading report…</div>;
  }

  if (error || !report) {
    return (
      <div className="rr-detail-page">
        <div className="rr-detail-toolbar">
          <Link to={basePath} className="rr-back-link">
            ← Back to research reports
          </Link>
        </div>
        <div className="error">Error: {error || 'Report not found'}</div>
      </div>
    );
  }

  return (
    <div className="rr-detail-page">
      <div className="rr-detail-toolbar">
        <Link to={basePath} className="rr-back-link">
          ← Back to research reports
        </Link>
        <div className="rr-detail-toolbar-actions">
          <button type="button" className="rr-btn-sm rr-btn-table" onClick={handleArchiveToggle}>
            {report.workspace_archived === true ? 'Restore from archive' : 'Archive'}
          </button>
          <button type="button" className="rr-btn-sm rr-btn-delete" onClick={handleDelete}>
            Delete
          </button>
        </div>
      </div>

      <header className="rr-detail-head">
        <div>
          <h2 className="rr-detail-title">{profileHeading(report, profiles)}</h2>
          <div className="rr-meta rr-detail-meta">
            <span className="rr-pill-muted">#{report.id}</span>
            {(() => {
              const rc = report.run_context || {};
              if (rc.kind === 'content_cluster' || rc.foundation === 'existing_content') {
                return (
                  <span className="rr-pill rr-pill-cluster" title="From existing content">
                    Cluster
                    {rc.source?.url && (
                      <a href={rc.source.url} target="_blank" rel="noreferrer" className="rr-source-link">
                        {' '}
                        ↗
                      </a>
                    )}
                  </span>
                );
              }
              if (rc.kind === 'focused') {
                return (
                  <span className="rr-pill rr-pill-focused" title="Legacy focused run">
                    Focused
                    {rc.title ? `: ${rc.title}` : ''}
                  </span>
                );
              }
              if (rc.foundation === 'brief' || rc.kind === 'general') {
                return (
                  <span className="rr-pill rr-pill-brief" title="Research brief">
                    Brief
                  </span>
                );
              }
              return null;
            })()}
            {report.report_date && (
              <span className="rr-date">{new Date(report.report_date).toLocaleDateString()}</span>
            )}
            <span className={`rr-status rr-status-${report.status}`}>{report.status}</span>
            {report.workspace_archived === true && (
              <span className="rr-pill-soft">Archived</span>
            )}
          </div>
        </div>
      </header>

      <ResearchReportBody report={report} profiles={profiles} onRefresh={loadReport} />
    </div>
  );
}

export default ResearchReportDetailPage;
